import type { GradedAnswer, MarkedPiece } from "./answer-marks.js";
import { ratings } from "./rating.js";

/**
 * Marking the order a reader put a sentence's pieces in.
 *
 * **The site does not claim to know Chinese word order.** An order it accepts
 * earns 对. Any other order using every piece exactly once earns 还行, which
 * FSRS treats as a pass, and the marked card shows the sentence that was
 * wanted. Anything else earns 不对. A reader who put 这本书我看过 together for
 * 我看过这本书 has made a sentence of Chinese, and a log with no delete path is
 * the wrong place to call that a lapse. #546 dropped this card partly over that
 * near miss, and #852 is the answer to it.
 *
 * **An order judged and refused earns 不对.** A line of
 * `src/content/liju/liju-word-orders.jsonl` can say an order of every piece
 * says something other than the sentence, and that is a wrong answer however
 * many pieces it used.
 *
 * Review supplies the written order and the sentence's judged orders. Grammar
 * exercises supply the accepted ones and use `yufa-marking.ts` to decide
 * completion separately.
 * An unrecognised order's hard rating is a scheduling policy and does not
 * establish that the word order is grammatical.
 *
 * Pure, in the shape the other graders take. A press is pieces, an
 * arrangement and the orders it is weighed against, and nothing here reads a
 * row or the clock.
 */

/** What one arrangement is weighed against. */
export interface Arrangement {
  /** The pieces' characters, in the sentence's own order. */
  readonly pieces: readonly string[];

  /** The pieces' characters, in the order the reader put them. */
  readonly arranged: readonly string[];

  /**
   * Every order that earns 对, the sentence's own first.
   *
   * Each is the pieces' characters in that order. The marks are drawn against
   * whichever the reader matched, or the first where they matched none, which
   * is the sentence the marked card shows.
   */
  readonly accepted: readonly [readonly string[], ...(readonly string[])[]];

  /**
   * Orders of every piece that were judged to say something else, which earn
   * 不对. None where the sentence was judged in no order but its own.
   */
  readonly refused?: readonly (readonly string[])[];
}

/** A list of pieces in an order that ignores the order they came in. */
function sorted(pieces: readonly string[]): string {
  return pieces.toSorted().join("\n");
}

/** Whether two lists hold the same strings as many times each. */
function samePieces(one: readonly string[], other: readonly string[]): boolean {
  return one.length === other.length && sorted(one) === sorted(other);
}

/**
 * Each place in the arrangement, against the piece the sentence puts there.
 *
 * A place holding the piece the sentence holds there is right. A place holding
 * another is wrong, with the wanted piece under it. A short arrangement leaves
 * the rest missing. A place the sentence has nothing for cannot come of a form
 * this site drew, and is marked extra.
 */
function marks(
  arranged: readonly string[],
  wanted: readonly string[],
): readonly MarkedPiece[] {
  const places = Math.max(arranged.length, wanted.length);

  return Array.from({ length: places }, (_, at) => {
    const typed = arranged[at];
    const expected = wanted[at];

    if (typed === undefined) {
      return { mark: "missing", ...(expected !== undefined && { expected }) };
    }

    if (expected === undefined) {
      return { mark: "extra", typed };
    }

    return { mark: typed === expected ? "correct" : "wrong", typed, expected };
  });
}

/**
 * Mark one arrangement.
 *
 * Orders are compared by the characters they spell. Two pieces written alike
 * (了 twice in one sentence) can swap places and still be the sentence.
 *
 * @param arrangement - The pieces, the reader's order and the accepted orders.
 * @returns The rating, a mark on each place, and the accepted sentences.
 */
export function gradeArrangement(arrangement: Arrangement): GradedAnswer {
  const { arranged, accepted, refused = [] } = arrangement;
  const said = arranged.join("");
  const matched = accepted.find((order) => order.join("") === said);
  const rating = !samePieces(arranged, arrangement.pieces)
    ? ratings.again
    : matched !== undefined
      ? ratings.good
      : refused.some((order) => order.join("") === said)
        ? ratings.again
        : ratings.hard;

  return {
    rating,
    pieces: marks(arranged, matched ?? accepted[0]),
    accepted: accepted.map((order) => order.join("")),
  };
}
